import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { sentenceCase } from "change-case";
import { isEmpty, isNil, values } from "lodash";
import { Controller } from "react-hook-form";
import type { Control, FieldErrors } from "react-hook-form";
import ReactSelect, { type MultiValue, type SingleValue } from "react-select";
import {
  RateType,
  TariffGroupType,
  TariffType,
  TariffZoneType,
} from "../../../generated/graphql";
import { DragAndDropUpload } from "../../common/drag-and-drop-upload";
import type { ImportTariffFormValuesV2 } from "../../tariff-importer/use-import-tariff-form";

const styles = {
  errorText: { color: "#D32F2F" },
  fullWidthSelect: { width: "100%" },
  checkboxControl: { paddingLeft: 1 },
} satisfies Record<string, SxProps<Theme>>;

const unsupportedTariffZoneTypes = new Set([
  TariffZoneType.Zipcode,
  TariffZoneType.Universal,
]);
const unsupportedTariffGroupTypes = new Set([TariffGroupType.Transfer]);

type TariffStepProps = {
  readonly control: Control<ImportTariffFormValuesV2>;
  readonly errors: FieldErrors<ImportTariffFormValuesV2>;
  readonly tariffChainsEnabled: boolean;
  readonly contactUuids: string[] | undefined;
  readonly autoSelectAllServices: boolean;
  readonly autoSelectAllVehicleTypes: boolean;
  readonly tariffType: TariffType | undefined;
  readonly tariffCanHaveZoneGroup: boolean;
  readonly contactOptions: Array<{ value: string; label: string }>;
  readonly currentServices: Array<{ uuid: string; name: string }>;
  readonly currentVehicleTypes: Array<{ uuid: string; name: string }>;
  readonly fuelProfiles: Array<{ uuid: string; name?: string | null }>;
  readonly tariffZoneGroups: Array<{ id: string; name: string }>;
};

/**
 * Renders the tariff source step: the CSV upload field and a short help note
 * describing the expected CSV header and data-row format.
 *
 * @param props The presentational props lifted from the importer container.
 * @param props.control The react-hook-form control for the tariff form.
 */
export function renderSourceStep({
  control,
}: Pick<TariffStepProps, "control">) {
  return (
    <>
      <Grid item xs={12}>
        <Controller
          name="tariffUrl"
          control={control}
          render={({ field: { onChange, value } }) => {
            return (
              <DragAndDropUpload
                fileUrl={value}
                setPresignedUrl={(url: string) => {
                  onChange(url);
                }}
              />
            );
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body2">
          CSV format: the first line must be the header
          <code> COL 1,COL 2,...</code>, followed by data rows shaped like
          <code> FROM,TO,MIN,...,CAP</code>.
        </Typography>
      </Grid>
    </>
  );
}

/**
 * Renders the targeting step: the customers selector (only when tariff chains
 * are disabled), the services selector with its "All services" checkbox, and
 * the vehicle-types selector with its "All vehicle types" checkbox (only when
 * tariff chains are enabled).
 *
 * @param props The presentational props lifted from the importer container.
 */
export function renderTargetingStep({
  control,
  errors,
  tariffChainsEnabled,
  contactUuids,
  autoSelectAllServices,
  autoSelectAllVehicleTypes,
  contactOptions,
  currentServices,
  currentVehicleTypes,
}: Omit<
  TariffStepProps,
  "tariffType" | "tariffCanHaveZoneGroup" | "fuelProfiles" | "tariffZoneGroups"
>) {
  return (
    <>
      <Grid item xs={12}>
        {!tariffChainsEnabled && (
          <Controller
            name="contactUuids"
            control={control}
            render={({ field: { onChange }, fieldState: { error } }) => {
              return (
                <>
                  <InputLabel id="age">Customers</InputLabel>
                  <ReactSelect
                    isClearable
                    isMulti
                    menuPosition="fixed"
                    options={contactOptions}
                    placeholder="Select contact"
                    closeMenuOnSelect={false}
                    onChange={(
                      option: MultiValue<{ value: string; label: string }>,
                    ) => {
                      if (!isNil(option)) {
                        onChange(option.map((o) => o.value));
                      }
                    }}
                  />
                  {!isNil(error) && (
                    <FormHelperText sx={styles.errorText}>
                      {error.message}
                    </FormHelperText>
                  )}
                </>
              );
            }}
          />
        )}
      </Grid>
      <Grid item xs={12}>
        <Controller
          name="serviceUuids"
          control={control}
          render={({ field: { onChange, value } }) => {
            const selectedServiceUuids = new Set<string>(
              Array.isArray(value) ? value : [],
            );
            return (
              <>
                <InputLabel id="age">
                  Services
                  {isEmpty(contactUuids) &&
                    ` (Tariff will apply to these services${
                      tariffChainsEnabled ? "" : " for all customers"
                    })`}
                </InputLabel>
                <ReactSelect
                  isClearable
                  isMulti
                  menuPosition="fixed"
                  options={currentServices.map((service) => {
                    return {
                      value: service.uuid,
                      label: service.name,
                    };
                  })}
                  value={
                    autoSelectAllServices
                      ? null
                      : currentServices
                          .filter((service) =>
                            selectedServiceUuids.has(service.uuid),
                          )
                          .map((service) => ({
                            value: service.uuid,
                            label: service.name,
                          }))
                  }
                  placeholder="Select services"
                  closeMenuOnSelect={false}
                  isDisabled={autoSelectAllServices}
                  onChange={(
                    option: MultiValue<{ value: string; label: string }>,
                  ) => {
                    if (!isNil(option)) {
                      onChange(option.map((o) => o.value));
                    }
                  }}
                />
                {!isNil(errors.serviceUuids) && (
                  <FormHelperText sx={styles.errorText}>
                    {errors.serviceUuids.message}
                  </FormHelperText>
                )}
              </>
            );
          }}
        />
        <Controller
          control={control}
          name="autoSelectAllServices"
          defaultValue={false}
          render={({ field }) => (
            <FormControl fullWidth sx={styles.checkboxControl}>
              <FormControlLabel
                control={
                  <Checkbox checked={field.value} onChange={field.onChange} />
                }
                label="All services"
                disabled={isEmpty(contactUuids) && !tariffChainsEnabled}
              />
            </FormControl>
          )}
        />
      </Grid>
      {tariffChainsEnabled && (
        <Grid item xs={12}>
          <Controller
            name="vehicleTypeUuids"
            control={control}
            render={({ field: { onChange, value } }) => {
              const selectedVehicleTypeUuids = new Set<string>(
                Array.isArray(value) ? value : [],
              );
              return (
                <>
                  <InputLabel>
                    Vehicle types (Tariff will apply to these vehicle types)
                  </InputLabel>
                  <ReactSelect
                    isClearable
                    isMulti
                    menuPosition="fixed"
                    options={currentVehicleTypes.map((vehicleType) => {
                      return {
                        value: vehicleType.uuid,
                        label: vehicleType.name,
                      };
                    })}
                    value={
                      autoSelectAllVehicleTypes
                        ? null
                        : currentVehicleTypes
                            .filter((vehicleType) =>
                              selectedVehicleTypeUuids.has(vehicleType.uuid),
                            )
                            .map((vehicleType) => ({
                              value: vehicleType.uuid,
                              label: vehicleType.name,
                            }))
                    }
                    placeholder="Select vehicle types"
                    closeMenuOnSelect={false}
                    isDisabled={autoSelectAllVehicleTypes}
                    onChange={(
                      option: MultiValue<{ value: string; label: string }>,
                    ) => {
                      if (!isNil(option)) {
                        onChange(option.map((o) => o.value));
                      }
                    }}
                  />
                  {!isNil(errors.vehicleTypeUuids) && (
                    <FormHelperText sx={styles.errorText}>
                      {errors.vehicleTypeUuids.message}
                    </FormHelperText>
                  )}
                </>
              );
            }}
          />
          <Controller
            control={control}
            name="autoSelectAllVehicleTypes"
            defaultValue={false}
            render={({ field }) => (
              <FormControl fullWidth sx={styles.checkboxControl}>
                <FormControlLabel
                  control={
                    <Checkbox checked={field.value} onChange={field.onChange} />
                  }
                  label="All vehicle types"
                />
              </FormControl>
            )}
          />
        </Grid>
      )}
    </>
  );
}

/**
 * Renders the tariff definition step: name, tariff zone type, unit type, rate
 * type, tariff group type, use-actual-weight, and zone group. Preserves the
 * disabling of unsupported zone/group options and the zone-group gating driven
 * by `tariffCanHaveZoneGroup`.
 *
 * @param props The presentational props lifted from the importer container.
 */
export function renderDefinitionStep({
  control,
  errors,
  tariffType,
  tariffCanHaveZoneGroup,
  tariffZoneGroups,
}: Pick<
  TariffStepProps,
  | "control"
  | "errors"
  | "tariffType"
  | "tariffCanHaveZoneGroup"
  | "tariffZoneGroups"
>) {
  return (
    <>
      <Grid item xs={4}>
        <Controller
          name="name"
          control={control}
          render={({ field: { onChange, value } }) => (
            <>
              <InputLabel>Tariff Name</InputLabel>
              <TextField
                fullWidth
                size="small"
                error={!isNil(errors.name)}
                value={value}
                onChange={onChange}
              />
              {!isNil(errors.name) && (
                <FormHelperText sx={styles.errorText}>
                  {errors.name.message}
                </FormHelperText>
              )}
            </>
          )}
        />
      </Grid>
      <Grid item xs={2}>
        <Controller
          name="tariffZoneType"
          control={control}
          render={({ field: { onChange, value } }) => (
            <>
              <InputLabel id="age">Tariff zone type</InputLabel>
              <Select
                size="small"
                value={value}
                sx={styles.fullWidthSelect}
                error={!isNil(errors.tariffZoneType)}
                onChange={onChange}
              >
                {values(TariffZoneType).map((type) => {
                  return (
                    <MenuItem
                      key={type}
                      value={type}
                      disabled={unsupportedTariffZoneTypes.has(type)}
                    >
                      {sentenceCase(type)}
                      {unsupportedTariffZoneTypes.has(type) && " (unsupported)"}
                    </MenuItem>
                  );
                })}
              </Select>
              {!isNil(errors.tariffZoneType) && (
                <FormHelperText sx={styles.errorText}>
                  {errors.tariffZoneType.message}
                </FormHelperText>
              )}
            </>
          )}
        />
      </Grid>
      <Grid item xs={2}>
        <Controller
          name="tariffType"
          control={control}
          render={({ field: { onChange, value } }) => (
            <>
              <InputLabel id="age">Unit type</InputLabel>
              <Select
                size="small"
                value={value}
                sx={styles.fullWidthSelect}
                error={!isNil(errors.tariffType)}
                onChange={onChange}
              >
                {values(TariffType).map((type) => {
                  return (
                    <MenuItem key={type} value={type}>
                      {sentenceCase(type)}
                    </MenuItem>
                  );
                })}
              </Select>
              {!isNil(errors.tariffType) && (
                <FormHelperText sx={styles.errorText}>
                  {errors.tariffType.message}
                </FormHelperText>
              )}
            </>
          )}
        />
      </Grid>
      <Grid item xs={2}>
        <Controller
          name="rateType"
          control={control}
          render={({ field: { onChange, value } }) => (
            <>
              <InputLabel id="age">Rate type</InputLabel>
              <Select
                size="small"
                value={value}
                sx={styles.fullWidthSelect}
                error={!isNil(errors.tariffType)}
                onChange={onChange}
              >
                {values(RateType)
                  .filter(
                    (rt) =>
                      (!isNil(tariffType) &&
                        tariffType !== TariffType.NoUnits) ||
                      rt === RateType.Flat,
                  )
                  .map((rateType) => {
                    return (
                      <MenuItem key={rateType} value={rateType}>
                        {rateType === RateType.QuantityBased
                          ? "Multiplier"
                          : sentenceCase(rateType)}
                      </MenuItem>
                    );
                  })}
              </Select>
              {!isNil(errors.tariffType) && (
                <FormHelperText sx={styles.errorText}>
                  {errors.tariffType.message}
                </FormHelperText>
              )}
            </>
          )}
        />
      </Grid>
      <Grid item xs={2}>
        <Controller
          name="tariffGroupType"
          control={control}
          render={({ field: { onChange, value } }) => (
            <>
              <InputLabel id="age">Tariff group type</InputLabel>
              <Select
                size="small"
                value={value}
                sx={styles.fullWidthSelect}
                error={!isNil(errors.tariffGroupType)}
                onChange={onChange}
              >
                {values(TariffGroupType).map((type) => {
                  return (
                    <MenuItem
                      key={type}
                      value={type}
                      disabled={unsupportedTariffGroupTypes.has(type)}
                    >
                      {sentenceCase(type)}
                      {unsupportedTariffGroupTypes.has(type) &&
                        " (unsupported)"}
                    </MenuItem>
                  );
                })}
              </Select>
              {!isNil(errors.tariffGroupType) && (
                <FormHelperText sx={styles.errorText}>
                  {errors.tariffGroupType.message}
                </FormHelperText>
              )}
            </>
          )}
        />
      </Grid>
      <Grid item xs={3}>
        <Controller
          control={control}
          name="useActualWeight"
          defaultValue={false}
          render={({ field }) => (
            <FormControl fullWidth sx={styles.checkboxControl}>
              <FormControlLabel
                control={
                  <Checkbox checked={field.value} onChange={field.onChange} />
                }
                label="Use Actual Weight"
              />
            </FormControl>
          )}
        />
      </Grid>
      <Grid item xs={3}>
        <Controller
          name="tariffZoneGroupId"
          control={control}
          render={({ field: { onChange, value } }) => (
            <>
              <InputLabel id="age">Zone Group</InputLabel>
              <ReactSelect
                isClearable
                menuPosition="fixed"
                value={
                  isNil(value)
                    ? null
                    : {
                        label: tariffZoneGroups.find(
                          (option) => option.id === value,
                        )?.name,
                        value,
                      }
                }
                options={tariffZoneGroups.map((option) => ({
                  label: option.name,
                  value: option.id,
                }))}
                placeholder="Select zone group"
                isDisabled={!tariffCanHaveZoneGroup}
                onChange={(
                  option: SingleValue<{
                    label: string | undefined;
                    value: string;
                  }>,
                ) => {
                  onChange(option?.value ?? null);
                }}
              />
              {!isNil(errors.tariffZoneGroupId) && (
                <FormHelperText sx={styles.errorText}>
                  {errors.tariffZoneGroupId.message}
                </FormHelperText>
              )}
            </>
          )}
        />
      </Grid>
    </>
  );
}

/**
 * Renders the schedule and fuel step: start date, end date, and the fuel
 * profile selector.
 *
 * @param props The presentational props lifted from the importer container.
 */
export function renderScheduleFuelStep({
  control,
  errors,
  fuelProfiles,
}: Pick<TariffStepProps, "control" | "errors" | "fuelProfiles">) {
  return (
    <>
      <Grid item xs={2}>
        <Controller
          name="startDate"
          control={control}
          render={({ field: { onChange, value } }) => (
            <>
              <InputLabel>Start date</InputLabel>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={value ?? ""}
                onChange={(event) => {
                  onChange(event.target.value);
                }}
              />
            </>
          )}
        />
      </Grid>
      <Grid item xs={2}>
        <Controller
          name="endDate"
          control={control}
          render={({ field: { onChange, value } }) => (
            <>
              <InputLabel>End date</InputLabel>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={value ?? ""}
                onChange={(event) => {
                  onChange(event.target.value);
                }}
              />
            </>
          )}
        />
      </Grid>
      <Grid item xs={4}>
        <Controller
          name="fuelProfileUuid"
          control={control}
          render={({ field: { onChange, value } }) => (
            <>
              <InputLabel id="age">Fuel profile</InputLabel>
              <Select
                size="small"
                value={value}
                sx={styles.fullWidthSelect}
                error={!isNil(errors.fuelProfileUuid)}
                onChange={onChange}
              >
                {fuelProfiles.map((fuelProfile) => {
                  return (
                    <MenuItem key={fuelProfile.uuid} value={fuelProfile.uuid}>
                      {fuelProfile.name}
                    </MenuItem>
                  );
                })}
              </Select>
              {!isNil(errors.fuelProfileUuid) && (
                <FormHelperText sx={styles.errorText}>
                  {errors.fuelProfileUuid.message}
                </FormHelperText>
              )}
            </>
          )}
        />
      </Grid>
    </>
  );
}
